import React, { Suspense, useEffect, useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import * as THREE from "three";

// ---------------------------------------------------------------------------
// Procedural human: anatomically proportioned body (torso lathe, two-segment
// limbs, hands, shoes, head), real fabric PBR texture on clothes, canvas-drawn
// face per skin tone, black crew t-shirt + black visor cap.
// Rig joints: shoulderL/R, elbowL/R, hipL/R, kneeL/R, spine, head.
// Axes (arm hangs along -Y at rest): shoulder.x < 0 = arm forward/up,
// shoulder.z > 0 = left arm outward (mirror for right). hip.x < 0 = thigh fwd.
// ---------------------------------------------------------------------------

const SKIN = { light: "#d9b08c", medium: "#c9a186", olive: "#a87c5f", dark: "#8a5f43" };

let fabricCache = null;
function getFabric() {
  if (fabricCache) return fabricCache;
  const L = new THREE.TextureLoader();
  const color = L.load("/tex/Fabric060_1K-JPG_Color.jpg");
  const normal = L.load("/tex/Fabric060_1K-JPG_NormalGL.jpg");
  const rough = L.load("/tex/Fabric060_1K-JPG_Roughness.jpg");
  [color, normal, rough].forEach((t) => {
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(3, 3);
  });
  color.colorSpace = THREE.SRGBColorSpace;
  fabricCache = { color, normal, rough };
  return fabricCache;
}

function clothMaterial(tint) {
  const f = getFabric();
  return new THREE.MeshStandardMaterial({
    map: f.color,
    normalMap: f.normal,
    roughnessMap: f.rough,
    normalScale: new THREE.Vector2(0.7, 0.7),
    color: new THREE.Color(tint),
    roughness: 1,
    metalness: 0,
  });
}

// --- full head textures: the AI portrait is baked into an equirectangular skin
// map (see /app/frontend/public/faces/head_*.jpg) so the face sits on the front
// of the skull and blends into skin and hair with no mask edge anywhere.
const HEAD_FILES = {
  light_m: "/faces/head_light_m.jpg",
  medium_m: "/faces/head_medium_m.jpg",
  olive_m: "/faces/head_olive_m.jpg",
  dark_m: "/faces/head_dark_m.jpg",
  light_f: "/faces/head_light_f.jpg",
  medium_f: "/faces/head_medium_f.jpg",
};
const headCache = {};

function headTexture(key) {
  if (headCache[key]) return headCache[key];
  const t = new THREE.TextureLoader().load(HEAD_FILES[key] || HEAD_FILES.medium_m);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  headCache[key] = t;
  return t;
}

function Torso({ mat }) {
  const geo = useMemo(() => {
    const pts = [
      new THREE.Vector2(0.128, -0.06),
      new THREE.Vector2(0.148, 0.08),
      new THREE.Vector2(0.15, 0.22),
      new THREE.Vector2(0.166, 0.36),
      new THREE.Vector2(0.148, 0.47),
      new THREE.Vector2(0.075, 0.53),
      new THREE.Vector2(0.05, 0.55),
    ];
    return new THREE.LatheGeometry(pts, 14);
  }, []);
  return <mesh castShadow geometry={geo} material={mat} />;
}

function Arm({ side, mat, skin }) {
  const s = side;
  const skinMat = <meshStandardMaterial color={skin} roughness={0.7} />;
  return (
    <group name={s > 0 ? "shoulderL" : "shoulderR"} position={[s * 0.2, 0.495, 0]}>
      {/* deltoid ball keeps the arm welded to the torso in every pose */}
      <mesh castShadow material={mat} scale={[1, 0.92, 1]}>
        <sphereGeometry args={[0.068, 14, 12]} />
      </mesh>
      {/* t-shirt sleeve */}
      <mesh castShadow position={[0, -0.1, 0]} material={mat}>
        <capsuleGeometry args={[0.066, 0.15, 4, 12]} />
      </mesh>
      {/* bare biceps below the sleeve */}
      <mesh castShadow position={[0, -0.225, 0]}>
        <capsuleGeometry args={[0.05, 0.14, 4, 12]} />
        {skinMat}
      </mesh>
      <group name={s > 0 ? "elbowL" : "elbowR"} position={[0, -0.3, 0]}>
        {/* elbow ball: fills the joint when the arm bends */}
        <mesh castShadow>
          <sphereGeometry args={[0.046, 14, 12]} />
          {skinMat}
        </mesh>
        <mesh castShadow position={[0, -0.13, 0]}>
          <capsuleGeometry args={[0.044, 0.2, 4, 12]} />
          {skinMat}
        </mesh>
        <mesh castShadow position={[0, -0.255, 0]}>
          <sphereGeometry args={[0.036, 10, 10]} />
          {skinMat}
        </mesh>
        <mesh castShadow position={[0, -0.295, 0.006]} scale={[0.82, 1.1, 0.5]}>
          <sphereGeometry args={[0.044, 12, 10]} />
          {skinMat}
        </mesh>
      </group>
    </group>
  );
}

function Leg({ side, mat }) {
  const s = side;
  const shoeMat = <meshStandardMaterial color="#16181c" roughness={0.6} />;
  return (
    <group name={s > 0 ? "hipL" : "hipR"} position={[s * 0.1, -0.03, 0]}>
      {/* hip ball welds the thigh to the pelvis */}
      <mesh castShadow material={mat} scale={[1, 0.9, 1]}>
        <sphereGeometry args={[0.082, 14, 12]} />
      </mesh>
      <mesh castShadow position={[0, -0.23, 0]} material={mat}>
        <capsuleGeometry args={[0.079, 0.32, 4, 12]} />
      </mesh>
      <group name={s > 0 ? "kneeL" : "kneeR"} position={[0, -0.45, 0]}>
        {/* knee ball: no hole when the leg bends */}
        <mesh castShadow material={mat}>
          <sphereGeometry args={[0.063, 14, 12]} />
        </mesh>
        <mesh castShadow position={[0, -0.21, 0]} material={mat}>
          <capsuleGeometry args={[0.062, 0.3, 4, 12]} />
        </mesh>
        {/* ankle */}
        <mesh castShadow position={[0, -0.395, 0]} material={mat}>
          <sphereGeometry args={[0.05, 12, 10]} />
        </mesh>
        {/* shoe: sole + upper + rounded toe */}
        <group position={[0, -0.455, 0.028]}>
          <mesh castShadow position={[0, 0.042, -0.01]}>
            <boxGeometry args={[0.092, 0.075, 0.15]} />
            {shoeMat}
          </mesh>
          <mesh castShadow position={[0, 0.032, 0.07]} scale={[1, 0.66, 1]}>
            <sphereGeometry args={[0.046, 12, 10]} />
            {shoeMat}
          </mesh>
          <mesh castShadow position={[0, -0.011, 0.012]}>
            <boxGeometry args={[0.101, 0.03, 0.25]} />
            <meshStandardMaterial color="#0a0b0d" roughness={0.9} />
          </mesh>
        </group>
      </group>
    </group>
  );
}

// Real baseball cap model (Blender asset supplied by the client), finished in
// matte black. One geometry shared by the whole crew.
const CAP_MATERIAL = new THREE.MeshStandardMaterial({
  color: "#0b0d11",
  roughness: 0.78,
  metalness: 0.04,
  side: THREE.DoubleSide,
});

function CrewCap() {
  const { nodes } = useGLTF("/models/cap.glb");
  const geometry = useMemo(
    () => Object.values(nodes).find((n) => n.isMesh)?.geometry,
    [nodes]
  );
  if (!geometry) return null;
  // worn tilted slightly back, visor pointing forward (+Z)
  return (
    <group position={[0, 0.052, 0]} rotation={[-0.24, Math.PI / 2, 0]} scale={1.27}>
      <mesh castShadow geometry={geometry} material={CAP_MATERIAL} />
    </group>
  );
}

function Head({ skin, face, cap }) {
  const tex = useMemo(() => headTexture(face), [face]);
  return (
    <group name="head" position={[0, 0.66, 0]}>
      {/* neck */}
      <mesh castShadow position={[0, -0.05, 0]}>
        <cylinderGeometry args={[0.046, 0.052, 0.11, 12]} />
        <meshStandardMaterial color={skin} roughness={0.7} />
      </mesh>
      {/* skull with the face printed on it */}
      <mesh castShadow scale={[0.92, 1.05, 0.98]}>
        <sphereGeometry args={[0.115, 40, 28]} />
        <meshStandardMaterial map={tex} color="#ffffff" roughness={0.58} />
      </mesh>
      {/* ears */}
      {[-1, 1].map((sx) => (
        <mesh key={sx} position={[sx * 0.099, -0.016, 0.002]} scale={[0.26, 0.68, 0.44]}>
          <sphereGeometry args={[0.036, 10, 8]} />
          <meshStandardMaterial color="#8d6247" roughness={0.98} />
        </mesh>
      ))}
      {cap === "crew" && <CrewCap />}
      {cap === "cowboy" && (
        <group position={[0, 0.135, 0]}>
          {/* crown with a crease */}
          <mesh castShadow>
            <cylinderGeometry args={[0.088, 0.108, 0.17, 16]} />
            <meshStandardMaterial color="#4a3419" roughness={0.92} />
          </mesh>
          <mesh castShadow position={[0, 0.088, 0]} scale={[1, 0.45, 1]}>
            <sphereGeometry args={[0.088, 16, 10]} />
            <meshStandardMaterial color="#4a3419" roughness={0.92} />
          </mesh>
          {/* hat band */}
          <mesh position={[0, -0.062, 0]}>
            <cylinderGeometry args={[0.111, 0.113, 0.035, 18]} />
            <meshStandardMaterial color="#241a0e" roughness={0.9} />
          </mesh>
          {/* brim, upturned at the edge */}
          <mesh castShadow position={[0, -0.082, 0]}>
            <cylinderGeometry args={[0.2, 0.215, 0.022, 22]} />
            <meshStandardMaterial color="#4a3419" roughness={0.92} />
          </mesh>
          <mesh castShadow position={[0, -0.072, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.208, 0.016, 8, 24]} />
            <meshStandardMaterial color="#412d15" roughness={0.92} />
          </mesh>
        </group>
      )}
    </group>
  );
}

const POSES = {
  boom: {
    shoulderL: [-2.8, 0, 0.3], shoulderR: [-2.8, 0, -0.3],
    elbowL: [-0.3, 0, 0], elbowR: [-0.3, 0, 0],
    spine: [-0.08, 0, 0], head: [-0.3, 0, 0],
  },
  operator: {
    shoulderL: [-1.3, 0, 0.14], shoulderR: [-1.25, 0, -0.14],
    elbowL: [-0.55, 0, 0], elbowR: [-0.6, 0, 0],
    head: [0.06, 0.12, 0],
  },
  megaphone: {
    shoulderR: [-1.7, 0, -0.18], elbowR: [-1.35, 0, 0],
    shoulderL: [-0.12, 0, 0.1], elbowL: [-0.4, 0, 0],
    head: [-0.04, -0.1, 0],
  },
  folder: {
    shoulderL: [-0.9, 0, 0.12], elbowL: [-1.55, 0, 0],
    shoulderR: [-0.15, 0, -0.08], elbowR: [-0.3, 0, 0],
    head: [0.14, 0.1, 0],
  },
  photographer: {
    shoulderL: [-1.5, 0, 0.28], shoulderR: [-1.5, 0, -0.28],
    elbowL: [-1.2, 0, 0.3], elbowR: [-1.2, 0, -0.3],
    head: [0.04, 0, 0],
  },
  reach: {
    shoulderR: [-1.6, 0, -0.2], elbowR: [-0.15, 0, 0],
    shoulderL: [-0.1, 0, 0.12], head: [0.12, 0.2, 0],
  },
  seated: {
    hipL: [-1.55, 0, 0.05], hipR: [-1.5, 0, -0.05],
    kneeL: [1.5, 0, 0], kneeR: [1.55, 0, 0],
    shoulderL: [-0.65, 0, 0.1], shoulderR: [-0.6, 0, -0.1],
    elbowL: [-0.5, 0, 0], elbowR: [-0.55, 0, 0],
    spine: [0.07, 0, 0], head: [0.05, 0, 0],
  },
  jump: {
    shoulderL: [-0.6, 0, 1.15], shoulderR: [-0.45, 0, -1.25],
    elbowL: [-0.4, 0, 0], elbowR: [-0.3, 0, 0],
    hipL: [-1.0, 0, 0.15], kneeL: [1.35, 0, 0],
    hipR: [-0.3, 0, -0.2], kneeR: [0.85, 0, 0],
    spine: [-0.18, 0, 0],
  },
  akimbo: {
    shoulderL: [-0.15, 0, 0.5], elbowL: [-0.2, 0, -1.25],
    shoulderR: [-0.1, 0, -0.1], elbowR: [-0.3, 0, 0],
    head: [0, 0.22, 0],
  },
  crossed: {
    shoulderL: [-0.85, 0, 0.2], elbowL: [-0.4, 0, -1.3],
    shoulderR: [-0.85, 0, -0.2], elbowR: [-0.4, 0, 1.3],
    head: [0.03, -0.18, 0],
  },
  clapper: {
    shoulderL: [-0.78, 0, 0.2], elbowL: [-1.62, 0, -0.14],
    shoulderR: [-0.84, 0, -0.24], elbowR: [-1.74, 0, 0.16],
    spine: [-0.04, 0, 0], head: [-0.18, 0, 0],
  },
  idleA: { shoulderL: [-0.06, 0, 0.1], shoulderR: [-0.08, 0, -0.12], elbowL: [-0.25, 0, 0], elbowR: [-0.3, 0, 0], head: [0, 0.3, 0] },
  idleB: { shoulderL: [-0.12, 0, 0.14], shoulderR: [-0.04, 0, -0.08], elbowR: [-0.4, 0, 0], head: [0.08, -0.3, 0] },
  idleC: { shoulderL: [-0.3, 0, 0.12], elbowL: [-0.5, 0, 0], shoulderR: [-0.05, 0, -0.1], head: [-0.05, 0.45, 0] },
};

function BoomPoleProp() {
  const alu = <meshStandardMaterial color="#2a2d33" roughness={0.5} metalness={0.5} />;
  return (
    // the pole runs through both raised hands, tilted just enough to lift the
    // microphone end; the mic hangs from the far end, clamped under the tube
    <group position={[0, 2.013, 0.02]} rotation={[0, 0, -0.13]}>
      <mesh castShadow rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.017, 0.017, 2.9, 10]} />
        {alu}
      </mesh>
      {/* grip tape where the hands close on the pole */}
      {[-0.37, 0.37].map((x) => (
        <mesh key={x} position={[x, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.021, 0.021, 0.16, 10]} />
          <meshStandardMaterial color="#14161a" roughness={0.95} />
        </mesh>
      ))}
      {/* shock mount + microphone at the far end, square to the tube */}
      <group position={[-1.44, 0, 0]}>
        {/* clamp sitting on top of the tube */}
        <mesh castShadow position={[0, 0.006, 0]}>
          <boxGeometry args={[0.055, 0.05, 0.05]} />
          <meshStandardMaterial color="#1c1f24" roughness={0.6} metalness={0.4} />
        </mesh>
        {/* suspension ring around the mic body */}
        <mesh position={[0, -0.06, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.045, 0.006, 6, 16]} />
          <meshStandardMaterial color="#3a3d42" roughness={0.6} metalness={0.4} />
        </mesh>
        {/* mic body: vertical, hanging under the tube */}
        <mesh castShadow position={[0, -0.16, 0]}>
          <capsuleGeometry args={[0.032, 0.2, 6, 14]} />
          <meshStandardMaterial color="#35383d" roughness={0.85} />
        </mesh>
        {/* foam windshield tip */}
        <mesh castShadow position={[0, -0.3, 0]}>
          <sphereGeometry args={[0.036, 14, 12]} />
          <meshStandardMaterial color="#2a2c30" roughness={1} />
        </mesh>
        {/* cable running back along the pole */}
        <mesh position={[0.32, 0.028, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.006, 0.006, 0.6, 6]} />
          <meshStandardMaterial color="#0e0f12" roughness={0.9} />
        </mesh>
      </group>
    </group>
  );
}

function MegaphoneProp() {
  // anchored exactly on the raised right hand (computed from the "megaphone"
  // pose), so the fist grips the neck of the horn
  return (
    <group position={[-0.265, 1.787, 0.319]} rotation={[1.82, 0, 0]}>
      {/* neck the hand closes around */}
      <mesh castShadow position={[0, 0.035, 0]}>
        <cylinderGeometry args={[0.036, 0.042, 0.11, 12]} />
        <meshStandardMaterial color="#1c1f24" roughness={0.7} />
      </mesh>
      {/* horn: narrow end at the hand, bell pointing away */}
      <mesh castShadow position={[0, 0.195, 0]} rotation={[Math.PI, 0, 0]}>
        <coneGeometry args={[0.125, 0.3, 20, 1, true]} />
        <meshStandardMaterial color="#c8c9cd" roughness={0.4} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, 0.345, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.124, 0.009, 6, 22]} />
        <meshStandardMaterial color="#8d939c" metalness={0.7} roughness={0.35} />
      </mesh>
      {/* body + trigger behind the hand */}
      <mesh castShadow position={[0, -0.045, 0]}>
        <cylinderGeometry args={[0.048, 0.05, 0.08, 12]} />
        <meshStandardMaterial color="#20242a" roughness={0.65} />
      </mesh>
      <mesh castShadow position={[0, -0.04, 0.055]}>
        <boxGeometry args={[0.035, 0.05, 0.03]} />
        <meshStandardMaterial color="#8a1f18" roughness={0.6} />
      </mesh>
    </group>
  );
}

function FolderProp() {
  return (
    <mesh castShadow position={[0.12, 1.18, 0.3]} rotation={[0.4, 0.1, 0.1]}>
      <boxGeometry args={[0.24, 0.32, 0.03]} />
      <meshStandardMaterial color="#8a2f22" roughness={0.8} />
    </mesh>
  );
}

function CameraProp() {
  return (
    <group position={[0, 1.6, 0.34]}>
      <mesh castShadow>
        <boxGeometry args={[0.18, 0.12, 0.12]} />
        <meshStandardMaterial color="#141519" roughness={0.4} metalness={0.4} />
      </mesh>
      <mesh position={[0, 0, 0.1]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.04, 0.045, 0.09, 12]} />
        <meshStandardMaterial color="#0c0d10" roughness={0.3} metalness={0.6} />
      </mesh>
    </group>
  );
}


// --- clapperboard: hinged slate with an animated clap ---
let slateTex = null;
function getSlateTexture() {
  if (slateTex) return slateTex;
  const c = document.createElement("canvas");
  c.width = 512; c.height = 400;
  const g = c.getContext("2d");
  g.fillStyle = "#0a0a0b";
  g.fillRect(0, 0, 512, 400);
  // subtle scuffs
  for (let i = 0; i < 300; i++) {
    g.fillStyle = `rgba(255,255,255,${Math.random() * 0.05})`;
    g.fillRect(Math.random() * 512, Math.random() * 400, Math.random() * 14, 1.2);
  }
  g.strokeStyle = "#f3f4f6";
  g.lineWidth = 3;
  const rows = [70, 150, 230, 310];
  rows.forEach((y) => { g.beginPath(); g.moveTo(16, y); g.lineTo(496, y); g.stroke(); });
  g.beginPath(); g.moveTo(190, 70); g.lineTo(190, 150); g.stroke();
  g.beginPath(); g.moveTo(330, 70); g.lineTo(330, 150); g.stroke();
  g.beginPath(); g.moveTo(256, 230); g.lineTo(256, 310); g.stroke();
  g.fillStyle = "#f3f4f6";
  g.font = "600 20px Arial";
  const label = (txt, x, y) => g.fillText(txt, x, y);
  label("ROLL", 24, 96); label("SCENE", 200, 96); label("TAKE", 340, 96);
  label("PROD.", 24, 176);
  label("DIRECTOR", 24, 256); label("CAMERA", 266, 256);
  label("DATE", 24, 336); label("INT / EXT", 266, 336);
  g.font = "900 40px Arial";
  label("12", 30, 140); label("4A", 206, 140); label("3", 346, 140);
  g.font = "900 34px Arial";
  label("NESSUN DOMANI", 30, 218);
  g.font = "900 30px Arial";
  label("D. CAMPAGNA", 30, 300); label("A CAM", 272, 300);
  g.font = "600 26px Arial";
  label("11.10", 30, 378); label("INT — NIGHT", 272, 378);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  slateTex = t;
  return t;
}

let stripeTex = null;
function getStripeTexture() {
  if (stripeTex) return stripeTex;
  const c = document.createElement("canvas");
  c.width = 512; c.height = 64;
  const g = c.getContext("2d");
  g.fillStyle = "#0a0a0b";
  g.fillRect(0, 0, 512, 64);
  for (let i = 0; i < 9; i++) {
    g.fillStyle = "#f3f4f6";
    g.save();
    g.translate(i * 58, 0);
    g.transform(1, 0, -0.42, 1, 0, 0);
    g.fillRect(0, 0, 29, 64);
    g.restore();
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  stripeTex = t;
  return t;
}

function ClapperProp() {
  const stick = useRef(null);
  const slate = useMemo(getSlateTexture, []);
  const stripes = useMemo(getStripeTexture, []);
  useFrame(({ clock }) => {
    if (!stick.current) return;
    const t = clock.elapsedTime % 3.4;
    let a = 0;
    if (t < 1.4) a = 0.8 * (t / 1.4) ** 0.7;
    else if (t < 1.52) a = 0.8 * (1 - (t - 1.4) / 0.12);
    stick.current.rotation.z = a;
  });
  return (
    <group position={[0.02, 1.4, 0.36]} rotation={[0.1, 0, -0.05]}>
      {/* slate body */}
      <mesh castShadow>
        <boxGeometry args={[0.32, 0.25, 0.016]} />
        <meshStandardMaterial color="#0a0a0b" roughness={0.42} />
      </mesh>
      <mesh position={[0, 0, 0.009]}>
        <planeGeometry args={[0.31, 0.24]} />
        <meshStandardMaterial map={slate} roughness={0.38} />
      </mesh>
      {/* fixed lower stripe bar */}
      <mesh castShadow position={[0, 0.142, 0]}>
        <boxGeometry args={[0.32, 0.034, 0.018]} />
        <meshStandardMaterial map={stripes} roughness={0.45} />
      </mesh>
      {/* hinged clapstick */}
      <group ref={stick} position={[-0.16, 0.162, 0.004]}>
        <mesh castShadow position={[0.16, 0.019, 0]}>
          <boxGeometry args={[0.32, 0.038, 0.016]} />
          <meshStandardMaterial map={stripes} roughness={0.45} />
        </mesh>
        <mesh position={[0, 0.019, 0]}>
          <cylinderGeometry args={[0.013, 0.013, 0.02, 8]} />
          <meshStandardMaterial color="#8d939c" metalness={0.8} roughness={0.3} />
        </mesh>
      </group>
    </group>
  );
}

function Human({ spec }) {
  const root = useRef(null);
  const shirtMat = useMemo(() => clothMaterial(spec.shirtTint || "#2b2f36"), [spec.shirtTint]);
  const pantsMat = useMemo(() => clothMaterial(spec.pantsTint || "#1e2126"), [spec.pantsTint]);
  useEffect(() => {
    const pose = POSES[spec.pose];
    if (!pose || !root.current) return;
    Object.entries(pose).forEach(([joint, rot]) => {
      const j = root.current.getObjectByName(joint);
      if (j) j.rotation.set(rot[0], rot[1], rot[2]);
    });
  }, [spec.pose]);

  return (
    <group position={spec.pos} rotation={[0, spec.rotY, 0]} scale={[spec.b, spec.h, spec.b]}>
      <group rotation={spec.tilt || [0, 0, 0]}>
        <group ref={root} position={[0, 0.96, 0]}>
          <group name="spine">
            <Torso mat={shirtMat} />
            {/* trapezius: smooths the torso-to-neck transition */}
            <mesh castShadow position={[0, 0.5, 0]} material={shirtMat} scale={[1, 0.52, 0.82]}>
              <sphereGeometry args={[0.135, 16, 12]} />
            </mesh>
            {/* pelvis */}
            <mesh castShadow position={[0, -0.05, 0]} material={pantsMat} scale={[1.12, 0.82, 0.96]}>
              <sphereGeometry args={[0.14, 16, 12]} />
            </mesh>
            <Arm side={1} mat={shirtMat} skin={spec.skin} />
            <Arm side={-1} mat={shirtMat} skin={spec.skin} />
            <Head skin={spec.skin} face={spec.face} cap={spec.cap} />
          </group>
          <Leg side={1} mat={pantsMat} />
          <Leg side={-1} mat={pantsMat} />
        </group>
      </group>
      {spec.prop === "boom" && <BoomPoleProp />}
      {spec.prop === "megaphone" && <MegaphoneProp />}
      {spec.prop === "folder" && <FolderProp />}
      {spec.prop === "camera" && <CameraProp />}
      {spec.prop === "clapper" && <ClapperProp />}
    </group>
  );
}

const SPEC = [
  { pos: [0.6, 0, -5.2], rotY: 2.9, pose: "boom", cap: "crew", prop: "boom", skin: SKIN.medium, face: "medium_m", h: 1.02, b: 1.0 },
  { pos: [-4, 0, 1], rotY: 1.2, pose: "folder", cap: "crew", prop: "folder", skin: SKIN.light, face: "light_m", h: 0.97, b: 0.94 },
  { pos: [1.8, 0, 0.6], rotY: -2.6, pose: "megaphone", cap: "crew", prop: "megaphone", skin: SKIN.olive, face: "olive_m", h: 1.0, b: 1.1 },
  { pos: [-4.03, 0, -4.53], rotY: 1.57, pose: "operator", cap: "crew", skin: SKIN.dark, face: "dark_m", h: 1.04, b: 1.0 },
  { pos: [4.13, -0.28, -0.386], rotY: 2.24, pose: "seated", cap: "crew", skin: SKIN.light, face: "light_f", h: 0.99, b: 0.96 },
  { pos: [4.692, -0.312, 0.319], rotY: 2.24, pose: "seated", cap: "crew", skin: SKIN.dark, face: "dark_m", h: 1.03, b: 1.04 },
  { pos: [7, 0, 4.5], rotY: -2.8, pose: "photographer", cap: "crew", prop: "camera", skin: SKIN.medium, face: "medium_f", h: 0.98, b: 1.0 },
  { pos: [2.4, 0, -5.4], rotY: 0.4, pose: "akimbo", cap: "cowboy", skin: SKIN.medium, face: "medium_m", shirtTint: "#6e4a26", pantsTint: "#3b2d1e", h: 1.01, b: 1.06 },
  { pos: [3.6, 0, -4.7], rotY: -0.5, pose: "crossed", cap: "cowboy", skin: SKIN.light, face: "light_m", shirtTint: "#54341c", pantsTint: "#2e2418", h: 1.05, b: 0.95 },
  { pos: [-8.5, 0, 0.8], rotY: 1.9, pose: "idleA", cap: "crew", skin: SKIN.olive, face: "olive_m", h: 1.0, b: 1.0 },
  { pos: [-8, 0, -3.2], rotY: -1.2, pose: "idleB", skin: SKIN.dark, face: "dark_m", h: 1.06, b: 1.1 },
  { pos: [-10, 0.62, -2.9], rotY: -0.6, pose: "jump", tilt: [0.55, 0, -0.1], skin: SKIN.medium, face: "medium_m", h: 1.0, b: 0.98 },
  { pos: [11.1, 0, 4.02], rotY: -2.64, pose: "reach", cap: "crew", skin: SKIN.light, face: "light_f", h: 0.96, b: 0.93 },
  { pos: [21.5, 0, -4.6], rotY: 0.6, pose: "idleC", cap: "crew", skin: SKIN.medium, face: "medium_m", h: 0.99, b: 1.0 },
  { pos: [6.1, 0, -0.15], rotY: -1.85, pose: "idleB", cap: "crew", skin: SKIN.olive, face: "olive_m", h: 1.01, b: 1.05 },
  { pos: [2.4, 0, -2.4], rotY: -0.09, pose: "clapper", cap: "crew", prop: "clapper", skin: SKIN.light, face: "light_m", h: 1.0, b: 0.98 },
];

export function Crew() {
  return (
    <Suspense fallback={null}>
      <group>
        {SPEC.map((s, i) => (
          <Human key={i} spec={s} />
        ))}
      </group>
    </Suspense>
  );
}

useGLTF.preload("/models/cap.glb");
